import algoliasearch from "algoliasearch/lite";
import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import {
  InstantSearch,
  Configure,
  useInfiniteHits,
  useInstantSearch,
  SortBy,
  useRefinementList,
  useCurrentRefinements,
} from "react-instantsearch-hooks-web";
import styles from "./Reviews.module.scss";
import Button from "../Button";

import RelaxedIcon from "../../components/icons/feelings_activities/relaxed.svg";
import BlissfulIcon from "../../components/icons/feelings_activities/blissful.svg";
import CreativeIcon from "../../components/icons/feelings_activities/pencil.svg";
import SleepyIcon from "../../components/icons/feelings_activities/sleep.svg";
import PainFreeIcon from "../../components/icons/feelings_activities/smile.svg";
import EnergeticIcon from "../../components/icons/feelings_activities/bolt.svg";
import HungryIcon from "../../components/icons/feelings_activities/pizza.svg";
import NotHighIcon from "../../components/icons/feelings_activities/not-high.svg";

import EaseMindIcon from "../../components/icons/feelings_activities/lotus.svg";
import GetReliefIcon from "../../components/icons/feelings_activities/medical.svg";
import GetSleepIcon from "../../components/icons/feelings_activities/snooze.svg";
import HangFriendsIcon from "../../components/icons/feelings_activities/friends.svg";
import StimulateMindIcon from "../../components/icons/feelings_activities/idea.svg";
import GetActiveIcon from "../../components/icons/feelings_activities/bolt.svg";
import GetIntimateIcon from "../../components/icons/feelings_activities/heart.svg";

import Star from "../../components/icons/star.svg";
import HalfStar from "../../components/icons/star-half.svg";
import Xmark from "../../components/icons/xmark.svg";
import Upvote from "../../components/icons/upvote.svg";

const searchClient = algoliasearch(
  process.env.NEXT_PUBLIC_ALGOLIA_ID,
  process.env.NEXT_PUBLIC_ALGOLIA_KEY
);

export default function ProductReviews({ rating, review_count }) {
  const router = useRouter();
  const product_id = router.query.id;

  if (!rating) {
    return null;
  }
  
  return (
    <div className="container pt-2 pb-2" id="reviews">
    <div className={styles.reviewsContainer}>
      <InstantSearch
        searchClient={searchClient}
        indexName={"product-reviews-production"}
      >
        <Configure
          hitsPerPage={5}
          filters={`product_id:${product_id}`}
          disjunctiveFacets={["feelings", "activities", "rating"]}
        />
        <Header rating={rating} review_count={review_count} />
        <CustomInfiniteHits />
      </InstantSearch>
    </div>
    </div>
  );
}

function CustomInfiniteHits(props) {
  const { hits, showMore, isLastPage } = useInfiniteHits(props);

  return (
    <div className={styles.reviews}>
      <ol>
        {hits?.map((hit, index) => (
          <Review hit={hit} key={index} />
        ))}
      </ol>
      {!isLastPage && (
        <Button
          type="button"
          className="primary"
          onClick={showMore}
          disabled={isLastPage}
        >
          View more reviews
        </Button>
      )}
    </div>
  );
}

const feelingsIcons = {
  Relaxed: RelaxedIcon,
  Blissful: BlissfulIcon,
  Creative: CreativeIcon,
  Sleepy: SleepyIcon,
  "Pain free": PainFreeIcon,
  Energetic: EnergeticIcon,
  Hungry: HungryIcon,
  "Not high": NotHighIcon,
};

const activitiesIcons = {
  "Ease my mind": EaseMindIcon,
  "Get relief": GetReliefIcon,
  "Get some sleep": GetSleepIcon,
  "Hang with friends": HangFriendsIcon,
  "Stimulate my mind": StimulateMindIcon,
  "Get Active": GetActiveIcon,
  "Get intimate": GetIntimateIcon,
};

const Header = ({ rating, review_count }) => {
  const { results } = useInstantSearch();

  if (!results || results.nbHits === 0) {
    return null;
  }

  return (
    <>
      <h2>Customer Reviews</h2>
      <div className={styles.refinements}>
        <div className={styles.starsContainer}>
          <div className={styles.reviews__header}>
            <div className={styles.aggregate_rating}>
              {rating}
              <div className={styles.stars}>{renderStars(rating)}</div>
            </div>

            <div className={styles.count}>{review_count} Reviews</div>
          </div>
          <StarRatingRefinement />
        </div>

        <FeelingsActivitiesRefinement />
      </div>
      <div className={styles.reviews__header}>
        <h3>{results.nbHits} Reviews</h3>
        <SortBy
          defaultRefinement="product-reviews-production"
          items={[
            { value: "product-reviews-production", label: "Recent" },
            { value: "product-reviews-by-rating-production", label: "Rating" },
            // { value: 'product-reviews-by-upvote-production', label: 'Upvote' }
          ]}
        />
      </div>
      <CurrentRefinementsList />
    </>
  );
};

const Review = ({ hit }) => {
  const name = hit.nickname?.replace(/^./, (char) => char.toUpperCase()) || "";

  return (
    <div className={styles.review}>
      <div className={styles.review__header}>
        <Avatar nickname={hit.nickname} />
        <div>
          <span className={styles.review__nickname}>{name}</span>
          <div className={styles.review__rating}>
            <div className={styles.stars}>{renderStars(hit.rating)}</div>
            <div className={styles.rating}>{hit.rating.toFixed(1)}</div>
          </div>
        </div>
      </div>

      {hit.comment && <p className={styles.review__comment}>{hit.comment}</p>}

      <div className={styles.review__footer}>
        {(hit.feelings?.length > 0 || hit.activities?.length > 0) && (
          <div className={styles.review__tags}>
            {hit.activities.map((activity, index) => {
              const IconComponent = activitiesIcons[activity];
              return (
                <span key={index} className={styles.iconTag}>
                  {IconComponent && (
                    <IconComponent className={styles.iconSvg} />
                  )}
                  {activity}
                </span>
              );
            })}
            {hit.feelings.map((feeling, index) => {
              const IconComponent = feelingsIcons[feeling];
              return (
                <span key={index} className={styles.iconTag}>
                  {IconComponent && (
                    <IconComponent className={styles.iconSvg} />
                  )}
                  {feeling}
                </span>
              );
            })}
          </div>
        )}
        <span className={styles.date}>
          Verified purchase, {new Date(hit.created_at).toLocaleDateString()}
        </span>
        {/* {hit.upvote_count > 0 && (
          <span className={styles.upvoteCount}>
            <Upvote /> {hit.upvote_count}
          </span>
        )} */}
      </div>
    </div>
  );
};

const renderStars = (rating) => {
  return Array.from({ length: 5 }, (_, index) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating - fullStars >= 0.5;

    if (index < fullStars) {
      // Full star (filled color)
      return <Star key={index} className={`${styles.star} ${styles.filled}`} />;
    } else if (index === fullStars && hasHalfStar) {
      // Half star (fixed color)
      return <HalfStar key={index} className={styles.star} />;
    } else {
      // Empty star (gray color)
      return <Star key={index} className={`${styles.star} ${styles.empty}`} />;
    }
  });
};

const Avatar = ({ nickname }) => {
  const initial = nickname ? nickname.charAt(0).toUpperCase() : "?";
  return <div className={styles.avatar}>{initial}</div>;
};

const StarRatingRefinement = () => {
  const { items, refine } = useRefinementList({
    attribute: "rating",
    operator: "or",
  });
  const { results } = useInstantSearch();
  const [globalRatingCounts, setGlobalRatingCounts] = useState(null);

  useEffect(() => {
    if (!globalRatingCounts && results?.disjunctiveFacets) {
      const ratingFacet = results.disjunctiveFacets.find(
        (f) => f.name === "rating"
      );
      if (ratingFacet && ratingFacet.data) {
        setGlobalRatingCounts(ratingFacet.data);
      }
    }
  }, [results]);

  if (!globalRatingCounts) return null; // Don't render until we have data

  const totalGlobalReviews = Object.values(globalRatingCounts).reduce(
    (sum, count) => sum + count,
    0
  );

  const toggleRefinement = (rating) => {
    const isRefined = items.some(
      (item) => parseInt(item.label) === rating && item.isRefined
    );
    refine(rating.toString());
  };

  return (
    <section aria-label="Star Rating Filter">
      {[5, 4, 3, 2, 1].map((rating) => {
        const globalCount = globalRatingCounts[rating] || 0;
        const globalPercentage =
          totalGlobalReviews > 0
            ? Math.round((globalCount / totalGlobalReviews) * 100)
            : 0;
        const isChecked = items.some(
          (i) => parseInt(i.label) === rating && i.isRefined
        );

        return (
          <div key={rating} className={styles.starRatingFilter}>
            <label
              className={`${styles.starRatingLabel} ${globalCount === 0 ? styles.disabled : ""}`}
            >
              <input
                type="checkbox"
                onChange={() => toggleRefinement(rating)}
                checked={isChecked}
                disabled={globalCount === 0}
                className={styles.starRatingCheckbox}
              />
              <span className={styles.starRatingText}>{rating} star</span>
              <div
                role="progressbar"
                aria-valuenow={globalPercentage}
                aria-valuemin="0"
                aria-valuemax="100"
                className={styles.starRatingBar}
              >
                <div
                  className={styles.starRatingFill}
                  style={{ width: `${globalPercentage}%` }}
                ></div>
              </div>
              <span className={styles.starRatingPercentage}>
                {globalPercentage}%
              </span>
            </label>
          </div>
        );
      })}
    </section>
  );
};

const FeelingsActivitiesRefinement = () => {
  // Get refinement lists
  const { items: feelingsItems, refine: refineFeelings } = useRefinementList({
    attribute: "feelings",
  });
  const { items: activitiesItems, refine: refineActivities } =
    useRefinementList({ attribute: "activities" });

  const { items: currentRefinements } = useCurrentRefinements();
  const [globalFeelings, setGlobalFeelings] = useState([]);
  const [globalActivities, setGlobalActivities] = useState([]);
  const [hasFetched, setHasFetched] = useState(false);

  // Fetch the top 3 most mentioned feelings and activities globally (static)
  useEffect(() => {
    if (!hasFetched && feelingsItems.length > 0 && activitiesItems.length > 0) {
      setGlobalFeelings(
        [...feelingsItems].sort((a, b) => b.count - a.count).slice(0, 3)
      );
      setGlobalActivities(
        [...activitiesItems].sort((a, b) => b.count - a.count).slice(0, 3)
      );
      setHasFetched(true);
    }
  }, [feelingsItems, activitiesItems, hasFetched]);

  const isSelected = (attribute, label) =>
    currentRefinements.some(
      (item) =>
        item.attribute === attribute &&
        item.refinements.some((ref) => ref.label === label)
    );

  return (
    <div className={styles.feelingsActivitiesFilter}>
      {/* Feelings Section */}
      <div className={styles.feelings}>
        <h3>Top feelings mentioned</h3>
        <div className={styles.filterGrid}>
          {globalFeelings.map((item) => {
            const IconComponent = feelingsIcons[item.label];

            return (
              <button
                key={item.label}
                onClick={() => refineFeelings(item.label)}
                className={`${styles.filterButton} ${isSelected("feelings", item.label) ? styles.selected : ""}`}
                aria-pressed={isSelected("feelings", item.label)}
                disabled={item.count === 0}
              >
                {IconComponent && <IconComponent className={styles.iconSvg} />}
                {item.label} ({item.count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Activities Section */}
      <div className={styles.activities}>
        <h3>Top activities mentioned</h3>
        <div className={styles.filterGrid}>
          {globalActivities.map((item) => {
            const IconComponent = activitiesIcons[item.label];

            return (
              <button
                key={item.label}
                onClick={() => refineActivities(item.label)}
                className={`${styles.filterButton} ${isSelected("activities", item.label) ? styles.selected : ""}`}
                aria-pressed={isSelected("activities", item.label)}
                disabled={item.count === 0}
              >
                {IconComponent && <IconComponent className={styles.iconSvg} />}
                {item.label} ({item.count})
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const CurrentRefinementsList = () => {
  const { items, refine } = useCurrentRefinements();

  if (items.length === 0) return null;

  return (
    <div className={styles.currentRefinements}>
      {items.flatMap((item) =>
        item.refinements.map((refinement) => (
          <Button
            key={refinement.label}
            type="button"
            onClick={() => refine(refinement)}
            className="currentRefinement"
            aria-label={`Remove ${refinement.label}`}
          >
            {item.attribute === "rating"
              ? `${refinement.label} star`
              : refinement.label}{" "}
            <Xmark />
          </Button>
        ))
      )}
    </div>
  );
};
